'use client';

import { Language, Subsidiary } from '@coolblue-development/constants';
import { Flex, freddyTheme, Tooltip, Typography } from '@coolblue-development/freddy';
import selectSwitchLanguageTrigger from '@coolblue-development/ga4-events/triggers/RecruitmentHeader/selectSwitchLanguageTrigger';
import { Checkmark } from '@coolblue-development/icons';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useRequestContext } from '@coolblue-development/next-request-context/client';
import { generateWebsiteUrl } from '@utils/url-generators/generateWebsiteUrl';
import Link from 'next/link';

import { PopoverForDisabledLanguage } from '../PopoverForDisabledLanguage';
import { tooltipTextMap } from '../tooltipText';

type Props = {
  languageOptionText: string;
  slug?: string;
  selectedLanguage: Language;
  isDisabled: boolean;
  isHamburgerVariant: boolean;
  isSelected: boolean;
  selectedSubsidiary: Subsidiary;
  isVacancyPage: boolean;
};

export function LanguageOption({
  languageOptionText,
  slug,
  selectedLanguage,
  isDisabled,
  isHamburgerVariant,
  isSelected,
  selectedSubsidiary,
  isVacancyPage
}: Readonly<Props>) {
  const { trackEvent } = useAnalytics();
  const { language: pageLanguage, subsidiary: pageSubsidiary, environment } = useRequestContext();

  const url =
    slug || !isVacancyPage
      ? generateWebsiteUrl(slug || '', selectedSubsidiary, selectedLanguage, environment)
      : '';

  const languageOptionContent = (
    <Flex
      key={languageOptionText}
      alignItems='center'
      justifyContent='space-between'
      aria-disabled={!!isDisabled}
      sx={{
        padding: `${freddyTheme.spacing[200]} ${freddyTheme.spacing[300]}`,
        backgroundColor: `${
          isSelected ? freddyTheme.color.background.interactiveSelected : 'transparent'
        }`,
        borderRadius: freddyTheme.borderRadius[100],
        '&:hover': {
          backgroundColor: freddyTheme.color.background[isDisabled ? 'base' : 'interactiveHovered']
        }
      }}
    >
      <Typography
        marginBottom={0}
        sx={{
          color: freddyTheme.color.font[isDisabled ? 'interactiveDisabled' : 'base']
        }}
      >
        {languageOptionText}
      </Typography>
      {isSelected && <Checkmark color={freddyTheme.color.icon.base} />}
      {isDisabled && isHamburgerVariant && (
        <PopoverForDisabledLanguage subsidiary={selectedSubsidiary} />
      )}
    </Flex>
  );

  if (isDisabled) {
    if (!isHamburgerVariant) {
      return (
        <Tooltip description={tooltipTextMap[selectedSubsidiary]} placement='left'>
          {languageOptionContent}
        </Tooltip>
      );
    }
    return languageOptionContent;
  }

  return (
    <Link
      href={url}
      style={{ textDecoration: 'none', color: 'inherit' }}
      onClick={e => {
        if (isSelected || (isVacancyPage && !slug)) {
          e.preventDefault();
          return;
        }
        trackEvent(
          selectSwitchLanguageTrigger({
            previous_language: `${pageSubsidiary}_${pageLanguage}`,
            new_language: `${selectedSubsidiary}_${selectedLanguage}`
          })
        );
        window.location.href = url;
      }}
    >
      {languageOptionContent}
    </Link>
  );
}
